import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Target, Trophy } from 'lucide-react'
import { HeroBanner } from './ui/HeroBanner'
import { AvailableExercises } from './ui/AvailableExercises'
import { exercisesAPI } from '@renderer/api/exercises'
import { coursesAPI } from '@renderer/api/courses'
import { useBreadcrumbs } from '@renderer/context/BreadcrumbContext'
import type { ExerciseDetail } from '../types/api'

interface ExerciseWithProgress extends ExerciseDetail {
  progress: number
  status: string
}

interface ExercisesProgressCardProps {
  completed: number
  total: number
  progressPct: number
}

const ExercisesProgressCard = ({ completed, total, progressPct }: ExercisesProgressCardProps): React.JSX.Element => (
  <div
    className="flex flex-col gap-3 px-8 py-6"
    style={{
      background: '#f5ede0',
      borderRadius: '16px',
      border: '1px solid #e0d4be',
      minWidth: '220px'
    }}
  >
    <Trophy className="w-6 h-6 text-[#7a6020]" />
    <div>
      <p className="text-sm text-gray-500 font-medium">Overall Progress</p>
      <div className="flex items-baseline gap-2">
        <p className="text-3xl font-bold text-gray-900">{progressPct}%</p>
      </div>
    </div>
    <div style={{ width: '100%', height: '6px', background: '#e0d4be', borderRadius: '3px' }}>
      <div style={{ width: `${progressPct}%`, height: '100%', background: '#7a6020', borderRadius: '3px' }} />
    </div>
    <p className="text-xs text-gray-500">{completed} of {total} exercises completed</p>
  </div>
)

export const ExercisesPage = (): React.JSX.Element => {
  const { courseId, moduleId } = useParams<{ courseId: string; moduleId: string }>()
  const { setBreadcrumbs } = useBreadcrumbs()
  const [moduleName, setModuleName] = useState<string>('')
  const [exercises, setExercises] = useState<ExerciseWithProgress[]>([])

  useEffect(() => {
    if (!moduleId || !courseId) return
    const fetchMeta = async () => {
      try {
        const [moduleData, courseData] = await Promise.all([
          exercisesAPI.getModule(moduleId),
          coursesAPI.getCourse(courseId)
        ])
        const mName = moduleData.title || 'Module'
        const cName = courseData.title || 'Course'
        setModuleName(mName)
        setBreadcrumbs([
          { label: 'Courses', path: '/main' },
          { label: cName, path: `/course/${courseId}/modules` },
          { label: mName }
        ])
      } catch {
        // ignore
      }
    }
    fetchMeta()
  }, [moduleId, courseId])

  const completedExercises = exercises.filter((e) => e.status === 'completed' || (e.progress ?? 0) >= 100).length
  const totalExercises = exercises.length
  const overallProgress =
    totalExercises > 0
      ? Math.round(exercises.reduce((sum, e) => sum + (e.progress ?? 0), 0) / totalExercises)
      : 0

  return (
    <div>
      <div className="max-w-[1200px] mx-auto space-y-6">
        <HeroBanner
          tag={
            <>
              <Target className="w-3.5 h-3.5" />
              Exercises
            </>
          }
          title={moduleName || 'Exercises'}
          description="Put your knowledge to the test with hands-on exercises. Complete each task step by step and build real-world skills that will set you apart."
          rightContent={
            totalExercises > 0 ? (
              <ExercisesProgressCard
                completed={completedExercises}
                total={totalExercises}
                progressPct={overallProgress}
              />
            ) : undefined
          }
        />
        <AvailableExercises moduleId={moduleId} courseId={courseId} onExercisesLoaded={setExercises} />
      </div>
    </div>
  )
}